// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFlyoutDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetFlyoutDrawerIsOpen";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import { getCurrentUserIsGuest, getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadCurrentBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadCurrentBillTo";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    getPageLinkByNodeId,
    getPageLinkByPageType,
    LinkModel,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import setIsReorderConfirmationModalOpen from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetIsReorderConfirmationModalOpen";
import setNextAction from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetNextAction";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { FloatingTooltip, FloatingTooltipStyles } from "@keller/App/Components/FloatingTooltip/FloatingTooltip";
import HeaderClickable from "@keller/App/Components/Header/HeaderClickable";
import MiniMyAccount from "@keller/App/Components/MiniMyAccount/MiniMyAccount";
import MiniSignIn from "@keller/App/Components/MiniSignIn/MiniSignIn";
import { CustomAppState } from "@keller/App/Store/Reducers";
import { xIsAuthenticated } from "@keller/App/Store/Selectors/GeneralSelectors";
import { EMPTY_VALUE } from "@keller/XDL/constants";
import Account from "@keller/XDL/Icons/Account";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    orderManagementSectionTitle = "orderManagementSectionTitle",
    orderManagementLinks = "orderManagementLinks",
    accountManagementTitle = "accountManagementTitle",
    accountManagementLinks = "accountManagementLinks",
}

const mapLinks = (state: ApplicationState, widgetLinks: LinkModel[]) => {
    const myAccountPage = getPageLinkByPageType(state, "MyAccountPage");
    return (
        widgetLinks
            ?.map(widgetLink => {
                const pageLink = getPageLinkByNodeId(state, widgetLink.fields.destination.value);
                if (pageLink) {
                    return { ...pageLink, title: widgetLink.fields.overriddenTitle || pageLink?.title };
                }
                return undefined;
            })
            ?.filter(pageLink =>
                pageLink?.parentId === myAccountPage?.id ? !pageLink?.excludeFromNavigation : true,
            ) || []
    );
};

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => {
    const orderManagementLinks = mapLinks(state, ownProps.fields.orderManagementLinks);
    const accountManagementLinks = mapLinks(state, ownProps.fields.accountManagementLinks);
    const linkGroups = [
        { title: ownProps.fields.accountManagementTitle, links: accountManagementLinks },
        { title: ownProps.fields.orderManagementSectionTitle, links: orderManagementLinks },
    ];
    return {
        isSignInPage: getCurrentPage(state).type === "SignInPage",
        userName: state.context.session.userName,
        myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        currentUserIsGuest: getCurrentUserIsGuest(state),
        isPunchOutSession: getIsPunchOutSession(state),
        currentLocation: getLocation(state),
        displayChangeCustomerLink: state.context.session?.displayChangeCustomerLink || false,
        // displayPricingAndInventory: state.context.session?.displayPricingAndInventory || false,
        // showInventory: state.context.showInventory,
        // showPricing: state.context.showPricing,
        isReorderConfirmationModalOpen: state.pages.vmiBins.isReorderConfirmationModalOpen,
        hasChangeLastQty:
            state.pages.vmiBins.selectVmiBinsChangedQty &&
            Object.keys(state.pages.vmiBins.selectVmiBinsChangedQty).length > 0,
        orderConfirmationPageUrl: getPageLinkByPageType(state, "OrderConfirmationPage")?.url,
        onePageCheckoutConfirmationPageUrl: getPageLinkByPageType(state, "OnePageCheckoutConfirmationPage")?.url,
        // xng-changes
        isAuthenticated: xIsAuthenticated(state),
        firstName: state.context.session.firstName,
        linkGroups,
        isTabletScreen: state.xCommon.isTabletScreen,
        currentBillTo: getCurrentBillToState(state).value,
        billToId: state.context.session.billToId,
        isSticky: state.xHeader.isSticky,
    };
};

const mapDispatchToProps = {
    signOut,
    loadCurrentBillTo,
    cancelPunchOut,
    setFlyoutDrawerIsOpen,
    setNavDrawerIsOpen,
    setIsReorderConfirmationModalOpen,
    setNextAction,
};

export interface HeaderSignInStyles {
    signOutWrapper?: InjectableCss;
    titleClickable?: ClickablePresentationProps;
    titleIcon?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
    signOutClickable?: ClickablePresentationProps;
    // XNG-Changes
    floatingTooltip?: FloatingTooltipStyles;
}

export const headerSignInStyles: HeaderSignInStyles = {
    signOutWrapper: {
        css: css`
            display: inline-flex;
        `,
    },
    titleClickable: {
        css: css`
            margin-left: 10px;
        `,
    },
    titleIcon: {
        size: 22,
    },
    titleTypography: {
        css: css`
            margin-left: 10px;
            max-width: 150px;
        `,
        ellipsis: true,
        transform: "uppercase",
    },
    signOutClickable: {
        css: css`
            flex: 0 0 auto;
            margin-left: 30px;
            text-transform: uppercase;
        `,
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.orderManagementSectionTitle]: string;
        [fields.orderManagementLinks]: LinkModel[];
        [fields.accountManagementTitle]: string;
        [fields.accountManagementLinks]: LinkModel[];
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

let icon: React.ComponentType<IconPresentationProps>;
export const HeaderSignIn = ({
    userName,
    isSignInPage,
    currentUserIsGuest,
    signOut,
    cancelPunchOut,
    fields,
    history,
    myAccountPageLink,
    signInUrl,
    isPunchOutSession,
    currentLocation,
    displayChangeCustomerLink,
    // XNG-Changes
    // displayPricingAndInventory,
    // showInventory,
    // showPricing,
    // setSignInOverlayIsOpen,
    // setShowInventory,
    // setShowPricing,
    // theme,
    isReorderConfirmationModalOpen,
    hasChangeLastQty,
    setIsReorderConfirmationModalOpen,
    setNextAction,
    orderConfirmationPageUrl,
    onePageCheckoutConfirmationPageUrl,
    // XNG-Changes
    isAuthenticated,
    firstName,
    id,
    linkGroups,
    isTabletScreen,
    billToId,
    currentBillTo,
    loadCurrentBillTo,
    setNavDrawerIsOpen,
    setFlyoutDrawerIsOpen,
    isSticky,
}: Props) => {
    const styles = useMergeStyles("headerSignIn", headerSignInStyles);
    // XNG-Changes
    const [isMenuOpen, setMenuOpen] = useState(false);

    // XNG-Changes
    useEffect(() => {
        if (billToId && !currentBillTo && isAuthenticated) {
            loadCurrentBillTo();
        }
    }, []);

    const onSignInHandler = (e: React.MouseEvent) => {
        // XNG-Changes
        if (isAuthenticated) {
            history.push(`/MyAccount`);
            return;
        }
        if ((!userName || currentUserIsGuest) && !isSignInPage) {
            if (!signInUrl) {
                logger.warn("No url was found for SignInPage, defaulting to /SignIn");
                history.push("/MyAccount/SignIn");
                return;
            }

            const returnUrl = currentLocation.pathname + currentLocation.search;
            if (
                returnUrl === "/" ||
                (currentUserIsGuest && orderConfirmationPageUrl && returnUrl.indexOf(orderConfirmationPageUrl) > -1) ||
                (currentUserIsGuest &&
                    onePageCheckoutConfirmationPageUrl &&
                    returnUrl.indexOf(onePageCheckoutConfirmationPageUrl) > -1)
            ) {
                history.push(`${signInUrl}`);
                return;
            }

            history.push(`${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect=true`);
            e.preventDefault();
        }
    };

    const onSignOutHandler = () => {
        if (!isReorderConfirmationModalOpen && hasChangeLastQty) {
            setIsReorderConfirmationModalOpen({ isReorderConfirmationModalOpen: true });
            setNextAction({ nextAction: signOutFunc });
        } else {
            signOutFunc();
        }
    };

    const signOutFunc = () => {
        isPunchOutSession ? cancelPunchOut() : signOut();
    };

    const closeNavigationDrawer = () => {
        setFlyoutDrawerIsOpen({ isOpen: false });
        setTimeout(() => setFlyoutDrawerIsOpen({ isOpen: undefined }), 300);
        setNavDrawerIsOpen({ navDrawerIsOpen: false });
        setTimeout(() => setNavDrawerIsOpen({ navDrawerIsOpen: undefined }), 300);
    };

    const headerElement = (
        <HeaderClickable
            isPopupShown={isMenuOpen}
            onClick={onSignInHandler}
            iconProps={{ src: Account }}
            data-test-selector="header_signIn"
            aria-label="SignIn"
        >
            <>
                {`${translate("Account")}`}
                {/* XNG-Changes */}
                {/* {showIcon && <Icon {...styles.titleIcon} src={icon} title={signInStatusText} />} */}
                <Typography className="sub-title">{`${
                    isAuthenticated ? translate(firstName || userName || EMPTY_VALUE) : translate("Register / Log In")
                }`}</Typography>
            </>
        </HeaderClickable>
    );

    if (isSignInPage) {
        return headerElement;
    }

    return (
        <StyledWrapper {...styles.signOutWrapper}>
            <FloatingTooltip
                extendedStyles={styles.floatingTooltip}
                onOpenChange={open => setMenuOpen(open)}
                triggerComponent={headerElement}
                trigger="hover"
                overlay={true}
                showArrow={!isTabletScreen}
                offset={{ mainAxis: isSticky ? 24 : 20, crossAxis: 0 }}
                placement="bottom-end"
                isOpen={isMenuOpen}
                isAuthenticated={isAuthenticated}
                shift={{ padding: 0 }}
                enabled={!isTabletScreen}
            >
                {isAuthenticated ? (
                    <MiniMyAccount linkGroups={linkGroups} onSignOutHandler={onSignOutHandler} />
                ) : (
                    <MiniSignIn />
                )}
            </FloatingTooltip>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(HeaderSignIn)),
    definition: {
        displayName: "Header Sign In",
        icon: "arrow-right-to-bracket",
        fieldDefinitions: [
            {
                name: fields.accountManagementTitle,
                fieldType: "Contextual",
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Account Management",
            },
            {
                displayName: "Links",
                name: fields.accountManagementLinks,
                editorTemplate: "ListField",
                fieldType: "Contextual",
                getDisplay: (item: HasFields, state) => {
                    const { type, value } = item.fields.destination;
                    const overriddenTitle = item.fields.overriddenTitle;
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return overriddenTitle || link?.title;
                    }
                },
                defaultValue: [],
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                        allowCategories: item => false,
                        allowUrls: item => false,
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
            {
                name: fields.orderManagementSectionTitle,
                fieldType: "Contextual",
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Product & Order Management",
            },
            {
                displayName: "Links",
                name: fields.orderManagementLinks,
                editorTemplate: "ListField",
                fieldType: "Contextual",
                getDisplay: (item: HasFields, state) => {
                    const { type, value } = item.fields.destination;
                    const overriddenTitle = item.fields.overriddenTitle;
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return overriddenTitle || link?.title;
                    }
                },
                defaultValue: [],
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                        allowCategories: item => false,
                        allowUrls: item => false,
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
